// Guide Block Types:
// - "section": { id: string, title: string, content: []}
// - "subsection": { title: string, content: [] }
// - "subdivision": { title: string, content: [] }
// - "text": { data: string | () => JSX }
// - "image": { data: string (url) }
// - "gallery": { images: [{ src, alt, credit: { name, url } }] }
// - "table": { data: [ { row: {} }]}
// - "list": { data: string (newline separated list) }
// - "component": { data: string (key to componentMap) }
// - "inlineLink": { text: string, onClick: fn }

const animalCareGuide = {
  id: "animalCare",
  title: "Animal Care Guide",
  category: "Guides",
  slug: "animalcare",
  sections: [
    {
      id: "adult-animals",
      title: "Adult Animals",
      type: "section",
      content: [
        {
          id: "what-are-adult-animals",
          title: "What Are Adult Animals?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `Adult Animals are limited items that are only available via specific acquisition methods, whether they're giveaways, gachas, or from limited purchases. They tend to be very hard to get.<br/>
              With the exception of Coops and Sluggeries, all other Adult Animals have very difficult ways to achieve them.<br/>
              <img src="https://pixelore.s3.eu-central-003.backblazeb2.com/Industries/AdultAnimals.png" class="h-96" alt="Adult Animals"/>`,
            },
          ],
        },
        {
          id: "how-to-acquire",
          title: "How do I get Adult Animals?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `Coops are NFT Traits, so the only way to get Adult chickens is to buy an NFT Land with a Coop on it.
              <br/>Sluggeries can be crafted via the Ministry of Innovation. You can buy the recipe at Bitsy's Blueprints, and then craft the Slug Hutch there.
              <br/>Turkeys, Cows, Apiaries, Sheep, Goats, Ducks, Dragons and Pigs can only be acquired via limited edition ways. Either by purchasing them when they're available in limited stock, or by winning them in events.
              <br/>You can also choose to P2P these items from other players!
              <br/><br/>It's worth knowing that every animal has a set limit so once that limit is reached, there won't ever be anymore of those animals added to the game! I'll try to mention those limits as much as I can below!`,
            },
            {
              type: "table",
              data: [
                {
                  animal: "Chicken",
                  limit: "NFT Trait",
                  "Total Distributed": "None",
                  distribution: "N/A",
                },
                {
                  animal: "Sluggery",
                  limit: "Can be Crafted - No Limit",
                  "Total Distributed": "None",
                  distribution: "Recipe can be bought in MOI",
                },
                {
                  animal: "Apiaries",
                  limit: "100",
                  "Total Distributed": "72",
                  distribution: "Coinbase Event: 50<br/>Public Auction: 1<br/>Pixels Trivia: 4<br/>Otherspeck: 10<br/>Carnival: 5<br/>Merchant Ships: 2",
                },
                {
                  animal: "Cows",
                  limit: "300",
                  "Total Distributed": "109",
                  distribution: "Winter Carnival 2024: 6<br/>Leaderboard Beta Event: 3<br/>Merchant Ships: 100",
                },
                {
                  animal: "Sheep",
                  limit: "300",
                  "Total Distributed": "146",
                  distribution: "Winter Carnival 2024: 36<br/>Zeller: 10<br/>Merchant Ships: 100",
                },
                {
                  animal: "Turkey",
                  limit: "300",
                  "Total Distributed": "128",
                  distribution: "Winter Carnival 2024: 18<br/>Zeller: 10<br/>Merchant Ships: 100",
                },
                {
                  animal: "Goats",
                  limit: "300",
                  "Total Distributed": "100",
                  distribution: "Merchant Ships: 100",
                },
                {
                  animal: "Ducks",
                  limit: "300",
                  "Total Distributed": "100",
                  distribution: "Merchant Ships: 100",
                },
                {
                  animal: "Pigs",
                  limit: "300",
                  "Total Distributed": "100",
                  distribution: "Merchant Ships: 100",
                },
                {
                  animal: "Dragons",
                  limit: "300",
                  "Total Distributed": "100",
                  distribution: "Merchant Ships: 100",
                },
              ],
            },
          ],
        },
        {
          id: "adult-vs-baby",
          title: "What does the Adult Animal Offer?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `The perks of the Adult Animals are they're forever! They never go away (unless you sell them or throw them away) and they're the only gateway to getting animal offspring.
              <br/>Adult Animals offer core item drops, as well as the offspring drops. They can be placed on Lands or Specks.<br/>
              <br>Note: Coops don't count towards animal care land limits, so you can have more animals on NFT lands with coops!`,
            },
          ],
        },
      ],
    },
    {
      id: "baby-animals",
      title: "Baby Animals/Offspring",
      type: "section",
      content: [
        {
          id: "what-are-baby-animals",
          title: "What Are Baby Animals?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `Baby Animals are offspring drops from Adult Animals that are only around for a certain amount of uses. After it's been used up, it'll die(?).<br/>
              Players can place these on NFT Lands and Specks, and they drop everything the Adult Animal does, except for Offspring.<br/>
              Some even drop extra things!`,
            },
          ],
        },
        {
          id: "how-to-get-baby-animal",
          title: "How do I get a Baby Animal?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `To get a baby animal, you first have to get an Offspring from an Adult Animal. All Adult Animals drop Offspring, so it's just a matter of chance when you're feeding and collecting from them!<br/>
              Once you have a baby animal, you'll need an Incubator and Incuvite Potions to hatch it.<br/>
              
              You can get an Incubator from the Pet Store. Once you have one, just place it on your land or speck and drop the Offspring inside the Incubator.<br/>

              <img src="https://pixelore.s3.eu-central-003.backblazeb2.com/Quest+Data/image_2026-01-22_025129373.png" alt="Pet Store Items" class="h-96 inline"/>
              <img src="https://pixelore.s3.eu-central-003.backblazeb2.com/Quest+Data/image_2026-01-22_025729234.png" alt="Incubator" class="h-60 inline"/><br/>
              <br/>The Incubator takes 5 Incuvite Potions with 18 Hours between each potion, for a total of 72 Hours. After you've placed all 5 Incuvite Potions, you'll be able to hatch your new Offspring!
              <br/>If you're super impatient, you can use Quicksilver and speed it up. O(∩_∩)O`,
            },
          ],
        },
        {
          id: "what-baby-animal-type",
          title: "Baby Animal Types",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `There are 4 Different types of Baby Animal you can get from each Offspring, each with their own features. (I'll Update features here when I know them!)<br/>
              The Incuvite Potions you use determine the type of baby animal you are likely to get. Using Basic Incuvite Potions increases your chances of getting a Tier 1 Baby Animal.<br/>
              Using Beast Incuvite Potions increase your chances of getting a Tier 4 Baby Animal.
              <br/><br/>According to the AMA, there's no guarantee chances of getting any no matter which potion you use, but you do increase your chance with using the right potions.
              <img src="https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/Incuvite+Potions.png" alt="Incuvite Potions" class="h-16"/><br/>`,
            },
          ],
        },
        {
          id: "incuvite-potions",
          title: "Incuvite Potions",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `There are four tiers of Incuvite Potions. Basic, Prism, Aurora and Beast. Each of these potions have a huge influence on the baby animal you produce from your offspring egg.<br/>`,
            },
            {
              type: "text",
              data: `To craft these potions, you need to find a Potion Table. A public Potion Table is inside the Pet Store. You can use that one.<br/>
                Use Salt to Trigger the Potion Table and drop items in there until the timer is up! I'm not sure of the recipes yet, but as we discover more info, I'll be adding it up here!`,
            },
          ],
        },
      ],
    },
    {
      id: "feeding-gathering",
      title: "Feeding and Gathering",
      type: "section",
      content: [
        {
          id: "feeding",
          title: "Feeding",
          type: "subdivision",
          content: [
            {
              type: "inlineLink",
              data: {
                text: ["Farmamix", "Apimix", "Algamix", "Choco Sauce", "Mystic Mix", "Empty Sauce Bottle"],
                itemId: ["itm_farmamix", "itm_apimix", "itm_algamix", "itm_choco_sauce", "itm_mysticMix", "itm_empty_choco_sauce"],
                description: `Every animal requires feeding to be able to give you something in return. But each animal can potentially want for something different! Here's a list of animals, their preferred food, and where to craft them!
                <br/><br/>
                <i>Tip: To see the recipe, click the food in the list below!</i>
                <br/>🐤 Chickens eat Farmamix and you can craft it at the Recycler
                <br/>🐝 Bees eat Apimix and you can craft it at the Recycler
                <br/>🦆 Ducks eat Algamix and you can craft it at the Recycler
                <br/>🐄 Cows eat Farmamix and you can craft it at the Recycler
                <br/>🐏 Sheeps eat Choco Sauce and you can craft an Empty Sauce Bottle at a Stove, then fill it up at any Chocolate Fountain
                <br/>🦃 Turkeys eat Farmamix and you can craft it at the Recycler
                <br/>🐌 Slugs eat Apimix eat Apimix and you can craft it at the Recycler
                <br/>🐲 Dragons eat Mystic Mix and you can craft it at the Recycler
                <br/>🐐 Goats eat Farmamix and you can craft it at the Recycle
                <br/>🐷 Pigs eat Choco Sauce and you can craft an Empty Sauce Bottle at a Stove, then fill it up at any Chocolate Fountain`,
              },
            },
          ],
        },
        {
          id: "gathering",
          title: "Gathering",
          type: "subdivision",
          content: [
            {
              type: "inlineLink",
              data: {
                text: ["Gathering Satchel"],
                itemId: ["itm_gatheringSatchel"],
                description: `When an animal has been fed, and is ready to collect, you'll also need a tool to collect what's pending (。﹏。*)
                <br/>You can get this tool from the Pet Store, and it's called a Gathering Satchel
                <br/>If you're <b>below level 5</b>, doing the <a href="/wiki/quests/81" target="_blank" ref="noreferrer" class="wiki-links">Wild Approach quest</a> is the best way to get a free Satchel!
                <br/><br/>After that, you can buy 1 Satchel for Coins at the Pet store, and every subsequent Satchel will need a Buoy Bucks purchase.`,
              },
            },
          ],
        },
      ],
    },
    {
      id: "drops",
      title: "Drops",
      type: "section",
      content: [
        {
          id: "animal-drops",
          title: "Animal Drops",
          type: "subdivision",
          content: [
            {
              type: "component",
              data: "petdrops",
            },
          ],
        },
      ],
    },
  ],
};

export default animalCareGuide;
